import React, { memo, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Stack,
  Stepper,
  Step,
  StepLabel,
  Button,
  LoadingButton,
  Icon,
  Typography,
  TextField,
  InputAdornment,
  Checkbox,
  CircularProgress,
  Select,
  MenuItem,
  Divider,
  Card,
  CardContent,
  FormControl,
  InputLabel
} from 'investira.react.components';
import { validators } from 'investira.sdk';
import { BaseTemplate } from '../../components/templates';
import { SearchAdd, EmpresaInfoSection, CvmSection } from '../../components/organisms';
import { InlineInfo, PessoaImage } from '../../components/molecules';
import services from '../../services';
import withMessage from '../../hoc/withMessage';
import AvalicoesSaveStatus from './_components/AvalicoesSaveStatus';
import { useAvaliacoesContext } from '../../contexts/AvaliacoesContext';
import AVALIACAO_MODELO, { AVALIACAO_MODELO_TIPO } from '../../enums/AVALIACAO_MODELO';
import { acPesquisasAtivosChanged, acPesquisasPessoasChanged } from '../../store/actions';

const AvaliacoesAdd = memo(props => {
  const navigate = useNavigate();
  const [isFetching, setFecthing] = useState();

  const { state, actions } = useAvaliacoesContext();
  const {
    steps,
    fundo,
    gestor,
    email_responsavel,
    assinatura_responsavel,
    requisitar_respostas,
    activeStepConfig,
    selectedTipo,
    selectedModelo
  } = state;

  const hasEmail = !validators.isEmpty(email_responsavel) && validators.isEmail(email_responsavel);

  const xStepsById = useMemo(() => {
    return steps.reduce((pAcc, pStep) => {
      pAcc[pStep.id] = pStep;
      return pAcc;
    }, {});
  }, [steps]);

  const xVisibleSteps = useMemo(() => {
    const xSteps = [{ id: 0, label: 'Tipo de Avaliação' }];

    if (validators.isEmpty(selectedTipo)) {
      return xSteps;
    }

    if (selectedTipo === AVALIACAO_MODELO_TIPO.fundo.id) {
      xSteps.push({ id: 1, label: 'Selecionar Modelo' });
    }

    xSteps.push({
      id: 2,
      label:
        selectedTipo === AVALIACAO_MODELO_TIPO.fundo.id ? 'Selecionar Fundo' : 'Selecionar Gestor'
    });
    xSteps.push({ id: 3, label: 'Confirmar Informações' });
    xSteps.push({ id: 4, label: 'Requisitar Respostas' });

    return xSteps;
  }, [selectedTipo]);

  const xActiveStepIndex = useMemo(() => {
    const xIndex = xVisibleSteps.findIndex(xStep => xStep.id === activeStepConfig);
    return xIndex === -1 ? 0 : xIndex;
  }, [xVisibleSteps, activeStepConfig]);

  const xModelosFundo = useMemo(() => {
    return Object.values(AVALIACAO_MODELO).filter(
      xModelo => xModelo.tipo === AVALIACAO_MODELO_TIPO.fundo.id
    );
  }, []);

  function getDisplayValue(pValue, pFallback = null) {
    if (pValue === null || pValue === undefined || pValue === '') {
      return pFallback;
    }

    if (typeof pValue === 'object') {
      return (
        pValue.pessoa_fantasia ||
        pValue.pessoa ||
        pValue.cnpj ||
        pValue.top_setor ||
        pValue.setor ||
        pFallback
      );
    }

    return String(pValue);
  }

  function handleUpdateGestor(pPessoa) {
    setFecthing(true);

    services.pessoas.list(
      { pessoa_id: pPessoa.pessoa_id },
      rRes => {
        const xGestor = rRes?.data?.[0];

        if (validators.isEmpty(xGestor)) {
          actions.handleChangeGestor({});
          actions.handleChangeStep(2, { completed: false });
          actions.handleChangeStep(3, { completed: false });
          actions.handleChangeStep(4, { completed: false });
          actions.handleChangeActiveStepConfig(2);
          props.onMessageError && props.onMessageError('Gestor não encontrado');
          return;
        }

        actions.handleChangeGestor(xGestor);
        actions.handleChangeFundo({});
        actions.handleChangeStep(2, { completed: true });
        actions.handleChangeStep(3, { completed: false });
        actions.handleChangeStep(4, { completed: false });
        actions.handleChangeActiveStepConfig(3);

        const xEmailResponsavel = xGestor?.cvm?.responsavel?.email;

        if (xEmailResponsavel) {
          actions.handleChangeEmailResponsavel(xEmailResponsavel);
          actions.handleChangeRequisitarRespostas(true);
        } else {
          actions.handleChangeEmailResponsavel('');
          actions.handleChangeRequisitarRespostas(false);
        }
      },
      rErr => {
        actions.handleChangeStep(2, { completed: false });
        actions.handleChangeStep(3, { completed: false });
        actions.handleChangeStep(4, { completed: false });
        actions.handleChangeActiveStepConfig(2);
        props.onMessageError && props.onMessageError('Falha na busca do gestor');
      },
      () => {
        setFecthing(false);
      }
    );
  }

  function handleUpdateFundo(pAtivo) {
    setFecthing(true);

    services.ativosbase.read(
      {
        ifeed_ativo_id: pAtivo.ativo_id,
        params: {}
      },
      rRes => {
        const xFundo = rRes?.data;

        if (validators.isEmpty(xFundo)) {
          actions.handleChangeFundo({});
          actions.handleChangeStep(2, { completed: false });
          actions.handleChangeStep(3, { completed: false });
          actions.handleChangeStep(4, { completed: false });
          actions.handleChangeActiveStepConfig(2);
          props.onMessageError && props.onMessageError('Fundo não encontrado');
          return;
        }

        actions.handleChangeFundo(xFundo);
        actions.handleChangeGestor({});
        actions.handleChangeStep(3, { completed: false });
        actions.handleChangeStep(4, { completed: false });
        actions.handleChangeStep(2, { completed: true });
        actions.handleChangeActiveStepConfig(3);

        if (xFundo.gestor_pessoa_id) {
          services.pessoas.list(
            { pessoa_id: xFundo.gestor_pessoa_id },
            rRes => {
              const xGestor = rRes?.data?.[0];

              if (validators.isEmpty(xGestor)) {
                props.onMessageError && props.onMessageError('Gestor não encontrado');
                return;
              }

              actions.handleChangeGestor(xGestor);

              const xEmailResponsavel = xGestor?.cvm?.responsavel?.email;

              if (xEmailResponsavel) {
                actions.handleChangeEmailResponsavel(xEmailResponsavel);
                actions.handleChangeRequisitarRespostas(true);
              } else {
                actions.handleChangeEmailResponsavel('');
                actions.handleChangeRequisitarRespostas(false);
              }
            },
            rErr => {
              props.onMessageError && props.onMessageError('Falha na busca do gestor');
            }
          );
        }
      },
      rErr => {
        actions.handleChangeStep(2, { completed: false });
        actions.handleChangeStep(3, { completed: false });
        actions.handleChangeStep(4, { completed: false });
        actions.handleChangeActiveStepConfig(2);
        props.onMessageError && props.onMessageError('Falha na busca do fundo');
      },
      () => {
        setFecthing(false);
      }
    );
  }

  const handleClearStateAndGoBack = () => {
    actions.handleResetState();
    navigate(-1);
  };

  const handleClearStateAndSend = () => {
    actions.handleResetState();
    props.onMessageSuccess && props.onMessageSuccess('Solicitação enviada com sucesso');
  };

  const isVerified = steps.find(xStep => xStep.id === 3)?.completed;

  const hasSelectedEntity =
    selectedTipo === AVALIACAO_MODELO_TIPO.fundo.id
      ? !validators.isEmpty(fundo)
      : !validators.isEmpty(gestor);

  const xFundoCards = useMemo(() => {
    return [
      {
        id: 'fundo-detalhes',
        title: 'Informações do Fundo',
        logoPessoaId: fundo?.logo_pessoa_id || fundo?.emissor?.logo_pessoa_id,
        alt: getDisplayValue(fundo?.ativo_seuid || fundo?.ativo, 'Fundo'),
        infos: [
          { label: 'Ativo', value: getDisplayValue(fundo?.ativo) },
          { label: 'ISIN', value: getDisplayValue(fundo?.isin) },
          { label: 'Desempenho', value: getDisplayValue(fundo?.fundo?.desempenho) },
          { label: 'Fechado', value: getDisplayValue(fundo?.fundo?.fechado), variant: 'bool' },
          { label: 'Situação', value: getDisplayValue(fundo?.fundo?.situacao) },
          { label: 'Taxa de Administração', value: getDisplayValue(fundo?.fundo?.adm) },
          { label: 'Taxa de Custódia', value: getDisplayValue(fundo?.fundo?.custodia_max) },
          { label: 'Mercado', value: getDisplayValue(fundo?.mercado_descricao) },
          { label: 'Tipo', value: getDisplayValue(fundo?.ativo_tipo) },
          { label: 'Emissor', value: getDisplayValue(fundo?.emissor?.pessoa_fantasia) },
          {
            label: 'Emissor CNPJ',
            value: getDisplayValue(fundo?.emissor?.cnpj),
            variant: 'cnpj'
          }
        ]
      }
    ];
  }, [fundo]);

  return (
    <BaseTemplate
      title={'Nova Avaliação'}
      subtitle={'Configuração'}
      description="Inicie um novo processo de due diligence em conformidade com os padrões ANBIMA QDD. Defina o tipo de avaliação, selecione a entidade, confirme as informações e solicite a colaboração."
      maxWidth={'xl'}
      headerRenderActions={
        <Stack direction="row" spacing={2}>
          <AvalicoesSaveStatus status={0} />
        </Stack>
      }>
      <Box sx={{ width: '100%' }}>
        <Box
          sx={{
            position: 'sticky',
            top: -24,
            zIndex: 1000,
            backgroundColor: 'background.dark',
            py: 2,
            mb: 2
          }}>
          <Stepper activeStep={xActiveStepIndex} sx={{ mb: 2 }}>
            {xVisibleSteps.map(xStep => {
              return (
                <Step key={xStep.id} completed={xStepsById[xStep.id]?.completed}>
                  <StepLabel>{xStep.label}</StepLabel>
                </Step>
              );
            })}
          </Stepper>
          <Divider />
        </Box>

        <Stack gap={2}>
          <Stack>
            <Stack mb={3}>
              <Stack direction="row" alignItems="center" gap={1}>
                <Typography variant="h6" color="textPrimary">
                  Tipo de Avaliação
                </Typography>
              </Stack>
              <Typography variant="body2" color="textPrimary">
                Defina se esta avaliação será de gestora ou de fundo.
              </Typography>
            </Stack>
            <Stack maxWidth="300px">
              <FormControl fullWidth>
                <Select
                  value={selectedTipo || ''}
                  onChange={pEvent => {
                    const xTipo = pEvent.target.value;

                    actions.handleChangeSelectedTipo(xTipo);
                    actions.handleChangeSelectedModelo('');
                    actions.handleChangeGestor({});
                    actions.handleChangeFundo({});
                    actions.handleChangeEmailResponsavel('');
                    actions.handleChangeRequisitarRespostas(false);
                    actions.handleChangeAssinaturaResponsavel(true);
                    actions.handleChangeStep(0, { completed: !validators.isEmpty(xTipo) });
                    actions.handleChangeStep(1, { completed: false });
                    actions.handleChangeStep(2, { completed: false });
                    actions.handleChangeStep(3, { completed: false });
                    actions.handleChangeStep(4, { completed: false });

                    if (xTipo === AVALIACAO_MODELO_TIPO.gestor.id) {
                      actions.handleChangeSelectedModelo(AVALIACAO_MODELO.modelo_gestora.id);
                      actions.handleChangeStep(1, { completed: true });
                      actions.handleChangeActiveStepConfig(2);
                      return;
                    }

                    if (xTipo === AVALIACAO_MODELO_TIPO.fundo.id) {
                      actions.handleChangeActiveStepConfig(1);
                      return;
                    }

                    actions.handleChangeActiveStepConfig(0);
                  }}
                  fullWidth
                  variant="outlined"
                  size="small"
                  displayEmpty
                  inputProps={{ 'aria-label': 'Tipo de Avaliação' }}>
                  <MenuItem value="" disabled>
                    Selecione um tipo
                  </MenuItem>
                  {Object.values(AVALIACAO_MODELO_TIPO).map(xTipo => {
                    return (
                      <MenuItem key={xTipo.id} value={xTipo.id}>
                        {xTipo.label}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </Stack>
          </Stack>

          <Divider />

          {selectedTipo === AVALIACAO_MODELO_TIPO.fundo.id && (
            <Stack>
              <Stack mb={3}>
                <Stack direction="row" alignItems="center" gap={1}>
                  <Typography variant="h6" color="textPrimary">
                    Modelo de Questionário
                  </Typography>
                </Stack>
                <Typography variant="body2" color="textPrimary">
                  Escolha abaixo o modelo de questionário desejado para esta avaliação.
                </Typography>
              </Stack>
              <Stack maxWidth="560px">
                <FormControl fullWidth>
                  <Select
                    value={selectedModelo || ''}
                    onChange={pEvent => {
                      const xModelo = pEvent.target.value;

                      actions.handleChangeSelectedModelo(xModelo);
                      actions.handleChangeGestor({});
                      actions.handleChangeFundo({});
                      actions.handleChangeEmailResponsavel('');
                      actions.handleChangeRequisitarRespostas(false);
                      actions.handleChangeStep(1, { completed: !validators.isEmpty(xModelo) });
                      actions.handleChangeStep(2, { completed: false });
                      actions.handleChangeStep(3, { completed: false });
                      actions.handleChangeStep(4, { completed: false });
                      actions.handleChangeActiveStepConfig(2);
                    }}
                    fullWidth
                    variant="outlined"
                    size="small"
                    displayEmpty
                    inputProps={{ 'aria-label': 'Modelo de Questionário' }}>
                    <MenuItem value="" disabled>
                      Selecione um modelo
                    </MenuItem>
                    {xModelosFundo.map(xModelo => {
                      return (
                        <MenuItem key={xModelo.id} value={xModelo.id}>
                          {xModelo.label}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              </Stack>
            </Stack>
          )}

          {selectedTipo === AVALIACAO_MODELO_TIPO.gestor.id && !validators.isEmpty(selectedModelo) && (
            <Stack mb={3}>
              <Stack mb={2}>
                <Typography variant="h5" color="textPrimary">
                  Escolha o Gestor
                </Typography>
              </Stack>
              <SearchAdd
                service={services.pesquisas.pessoa}
                params={{ sort: 'pessoa_fantasia' }}
                onClick={handleUpdateGestor}
                placeholder="Pesquise por gestor"
                variant="gestor"
              />
            </Stack>
          )}

          {selectedTipo === AVALIACAO_MODELO_TIPO.fundo.id && !validators.isEmpty(selectedModelo) && (
            <Stack mb={4}>
              <Stack mb={2}>
                <Typography variant="h5" color="textPrimary">
                  Escolha o Fundo
                </Typography>
              </Stack>
              <SearchAdd
                key={selectedTipo}
                service={services.pesquisas.ativo}
                params={{
                  mercado: 'FND',
                  visao: 254,
                  gestor_pessoa_id: '*',
                  fracionario: 0,
                  prazo: 'VST',
                  cancelado: 'null',
                  sort: 'relevancia DESC, __mr__ DESC, mercado ASC',
                  ...(AVALIACAO_MODELO[selectedModelo] && {
                    ativo_tipo_id: AVALIACAO_MODELO[selectedModelo].ativo_tipo_id.join(', ')
                  })
                }}
                onClick={handleUpdateFundo}
                placeholder="Pesquise por fundos"
                variant="ativo"
                store={false}
              />
            </Stack>
          )}

          {isFetching && !hasSelectedEntity && (
            <Stack gap={3}>
              <Stack
                gap={2}
                sx={{ minHeight: '200px', justifyContent: 'center', alignItems: 'center' }}>
                <CircularProgress />
              </Stack>
            </Stack>
          )}

          {hasSelectedEntity && (
            <Stack gap={1}>
              {selectedTipo === AVALIACAO_MODELO_TIPO.gestor.id ? (
                <Stack>
                  <Stack>
                    <Typography variant="h6" color="textPrimary">
                      Dados do Gestor
                    </Typography>
                  </Stack>
                  <Stack my={2} direction="row" gap={2}>
                    <EmpresaInfoSection pessoa={gestor} />
                    <CvmSection pessoa={gestor} />
                  </Stack>
                </Stack>
              ) : (
                <Stack>
                  <Stack>
                    <Typography variant="h6" color="textPrimary">
                      Dados do Fundo
                    </Typography>
                  </Stack>
                  <Stack my={2} flexGrow={1} sx={{ minWidth: '288px' }} component="section">
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', md: '2fr 1fr 1fr' },
                        gap: 2
                      }}>
                      {xFundoCards.map(xCard => {
                        return (
                          <Card key={xCard.id} sx={{ height: '100%' }}>
                            <CardContent
                              sx={{
                                height: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'space-between'
                              }}>
                              <Stack spacing={2} sx={{ py: 2 }}>
                                <Typography
                                  variant="subtitle1"
                                  color="textPrimary"
                                  sx={{ fontWeight: 600 }}
                                  component="h3">
                                  {xCard.title}
                                </Typography>
                                <Stack direction="row" alignItems="center" spacing={1.5}>
                                  <PessoaImage
                                    logoPessoaId={xCard.logoPessoaId}
                                    alt={xCard.alt}
                                    size={36}
                                  />
                                </Stack>

                                <Stack direction="column" sx={{ gap: 1 }}>
                                  {xCard.infos.map(xInfo => {
                                    return (
                                      <InlineInfo key={`${xCard.id}-${xInfo.label}`} {...xInfo} />
                                    );
                                  })}
                                </Stack>
                              </Stack>
                            </CardContent>
                          </Card>
                        );
                      })}
                      <EmpresaInfoSection pessoa={gestor} />
                      <CvmSection pessoa={gestor} />
                    </Box>
                  </Stack>
                </Stack>
              )}

              <Stack direction="row" justifyContent={'end'} mb={2}>
                <Button
                  variant={isVerified ? 'text' : 'contained'}
                  onClick={() => {
                    actions.handleChangeStep(3, { completed: true });
                    actions.handleChangeActiveStepConfig(4);
                  }}
                  color="primary"
                  startIcon={<Icon iconName="check" size={16} />}
                  disabled={isVerified}>
                  {isVerified
                    ? 'Verificado'
                    : selectedTipo === AVALIACAO_MODELO_TIPO.fundo.id
                    ? 'Confirmar fundo'
                    : 'Confirmar gestor'}
                </Button>
              </Stack>

              <Divider />

              {isVerified && !validators.isEmpty(selectedModelo) && (
                <>
                  <Stack my={2}>
                    <Stack mb={3}>
                      <Stack direction="row" alignItems="center" gap={1}>
                        <Typography variant="h6" color="textPrimary">
                          Requisitar respostas
                        </Typography>
                      </Stack>
                      <Typography variant="body2" color="textPrimary">
                        Enviaremos um link seguro para que o responsável preencha o questionário
                        QDD.
                      </Typography>
                    </Stack>
                    <Stack>
                      <Stack maxWidth="50%">
                        <TextField
                          label="Email do responsável"
                          placeholder="responsavel@instituicao.com.br"
                          type="email"
                          variant="outlined"
                          size="small"
                          value={email_responsavel}
                          onChange={pEvent =>
                            actions.handleChangeEmailResponsavel(pEvent.target.value)
                          }
                          InputLabelProps={{ shrink: true }}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <Icon iconName="mail" size={18} color="textSecondary" />
                              </InputAdornment>
                            )
                          }}
                          fullWidth
                        />
                        {/* <Stack my={1}>
                          <Stack direction="row" alignItems="center">
                            <Checkbox
                              id="requisitar-assinatura"
                              checked={assinatura_responsavel}
                              onChange={() =>
                                actions.handleChangeAssinaturaResponsavel(!assinatura_responsavel)
                              }
                              color="primary"
                              size="small"
                            />
                            <Typography
                              variant="body2"
                              color="textPrimary"
                              htmlFor="requisitar-assinatura">
                              Requisitar assinatura do responsável
                            </Typography>
                          </Stack>
                        </Stack> */}
                      </Stack>
                    </Stack>
                  </Stack>
                </>
              )}
            </Stack>
          )}

          <Stack direction="row">
            <Button variant="outlined" onClick={handleClearStateAndGoBack}>
              Cancelar
            </Button>
            <Stack flexGrow={1}></Stack>
            <Stack direction="row" gap={1}>
              <LoadingButton
                variant="outlined"
                color="primary"
                startIcon={<Icon iconName="upload" size={16} />} // Trocar o icone enquanto estiver atualizando passivamente
                size="small"
                sx={{ px: 2 }}
                type="submit"
                disabled={props.disabledButtons}
                //loading={isSubmitting}
              >
                {/* {isSubmitting ? "Salvando..." : "Salvar Rascunho"} */}
                Salvar Rascunho
              </LoadingButton>
              <Button
                variant="contained"
                disabled={!hasEmail || activeStepConfig !== 4}
                onClick={handleClearStateAndSend}
                color="primary"
                endIcon={<Icon iconName="arrow_right" size={16} />}>
                Enviar solicitação e Iniciar nova avaliação
              </Button>
              <Button
                variant="contained"
                disabled={!hasEmail || activeStepConfig !== 4}
                onClick={() => navigate('/avaliacoes/waiting/6d9c7884e5110addd38cdeb3ef2a0aab')}
                color="primary"
                endIcon={
                  email_responsavel ? (
                    <Icon iconName="paper_plane" size={16} />
                  ) : (
                    <Icon iconName="arrow_right" size={16} />
                  )
                }>
                Enviar solicitação
              </Button>
            </Stack>
          </Stack>
        </Stack>
      </Box>
    </BaseTemplate>
  );
});

AvaliacoesAdd.displayName = 'AvaliacoesAdd';

export default withMessage(AvaliacoesAdd);
